import axios from 'axios';
import router from 'next/router';
import { BaseURL } from './constants';
import { Modal, message } from 'antd';
import { MAINTENANCE_PATH } from '@/apis/MaintenanceApi';

axios.interceptors.request.use((config) => {
    if (config.headers) {
        config.headers['Content-Type'] = 'application/json';
        config.headers['Accept'] = 'application/json';
    }

    let url = config.url;
    if (config.method === 'get' && config.params && config.params.length > 0) {
        url += (url && url.indexOf('?') >= 0 ? '&' : '?');
        let keys = Object.keys(config.params);
        for (let key of keys) {
            url += `${key}=${encodeURIComponent(config.params[key])}&`;
        }
        url = url?.substring(0, url.length - 1);
        config.params = {};
    }

    if (url && url.startsWith('/')) url = BaseURL + url;

    config.url = url;

    config.withCredentials = true;

    return config;
});

axios.interceptors.response.use(
    function (response) {
        if (response.data.code === '401') {
            router.push({
                pathname: '/401',
            });
        }
        if (response.data.message === 'no') {
            router.push({
                pathname: '/403',
            });
        }
        return response;
    },
    function (error) {
        // Release maintenance (backend 503 MAINTENANCE): go to the maintenance page instead of
        // one error dialog per pending request. A bare 503 from the load balancer still shows the dialog.
        if (error.response?.status === 503 && error.response.data?.code === 'MAINTENANCE') {
            if (router.pathname !== MAINTENANCE_PATH) router.replace(MAINTENANCE_PATH);
            return error;
        }
        // The maintenance test account outside maintenance: one notice, back to the sign-in page.
        if (error.response?.status === 403 && error.response.data?.code === 'MAINTENANCE_ACCOUNT_ONLY') {
            message.warning({
                key: 'maintenance-account-only',
                content: 'このアカウントはメンテナンス中のみ利用できます / This account can be used only during maintenance.',
            });
            if (router.pathname !== '/login') router.replace('/login');
            return error;
        }
        let strMsg = '';
        if (error.response) {
            if (error.response.status === 504) {
                strMsg = `Gateway Timeout (504): The server, while acting as a gateway or proxy, did not receive a timely response from the upstream server.\nDetails:\n` +
                        `- Request URL: ${error.config?.url || 'Unknown URL'}\n` +
                        `- Request Method: ${error.config?.method?.toUpperCase() || 'Unknown Method'}\n` +
                        `- Request Time: ${new Date().toLocaleString()}`;
            } else {
                const statusMessages: Record<number, string> = {
                    400: 'Bad Request - The request was malformed or contains invalid parameters',
                    401: 'Unauthorized - Authentication is required or has failed',
                    403: 'Forbidden - You do not have permission to access this resource',
                    404: 'Not Found - The requested resource does not exist',
                    500: 'Internal Server Error - Something went wrong on the server',
                    502: 'Bad Gateway - The server received an invalid response from the upstream server',
                    503: 'Service Unavailable - The server is temporarily unable to handle the request'
                };

                const statusText = statusMessages[error.response.status] || 'Unknown Error';

                strMsg = `Request failed with status code ${error.response.status}: ${statusText}\n\n` +
                        `Details:\n` +
                        `- Request URL: ${error.config?.url || 'Unknown URL'}\n` +
                        `- Request Method: ${error.config?.method?.toUpperCase() || 'Unknown Method'}\n` +
                        `- Server Message: ${error.response.data?.message || 'No server message'}\n` +
                        `- Request Time: ${new Date().toLocaleString()}\n\n` +
                        `Please try again later or contact support if the problem persists.`;
            }
        } else if (error.request) {
            strMsg = `No response received from the server. This might be caused by:\n` +
                    `1. Network connectivity issues\n` +
                    `2. Server is down\n` +
                    `3. CORS configuration issues\n\n` +
                    `Request Details:\n` +
                    `- Request URL: ${error.config?.url || 'Unknown URL'}\n` +
                    `- Request Method: ${error.config?.method?.toUpperCase() || 'Unknown Method'}\n` +
                    `- Error Message: ${error.message || 'No error message'}\n` +
                    `- Request Time: ${new Date().toLocaleString()}\n\n` +
                    `Please check your network connection and contact system administrator if the problem persists.`;
        } else {
            strMsg = `Request failed to send. This might be caused by:\n` +
                    `1. Invalid request configuration\n` +
                    `2. Network initialization failed\n` +
                    `3. Client-side JavaScript error\n\n` +
                    `Error Details:\n` +
                    `- Error Message: ${error.message || 'No error message'}\n` +
                    `- Error Name: ${error.name || 'Unknown error type'}\n` +
                    `- Request URL: ${error.config?.url || 'Unknown URL'}\n` +
                    `- Request Method: ${error.config?.method?.toUpperCase() || 'Unknown Method'}\n` +
                    `- Request Time: ${new Date().toLocaleString()}\n\n` +
                    `Please check your request configuration and contact development team if needed.`;
        }

        strMsg && Modal.error({
            title: 'Error',
            content: strMsg,
            okText: 'OK',
            onOk: () => { },
        });

        return error;
    }
);

export default axios;
